import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Select, Image, Tag, Tabs, Upload, Card, List } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, QrcodeOutlined, EyeOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { doctorService, Doctor, DoctorQueryParams, DoctorQRCode, DoctorQRScanRecord } from '../services/doctorService';
import { getProjects, Project } from '../services/project';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axios';
import useResponsive from '../hooks/useResponsive';

const DoctorManagement: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [projects, setProjects] = useState<Project[]>([]);

  const [filters, setFilters] = useState<DoctorQueryParams>({});
  
  // 二维码相关状态
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [qrCodeData, setQrCodeData] = useState<DoctorQRCode | null>(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [scanRecords, setScanRecords] = useState<DoctorQRScanRecord[]>([]);
  const [scanRecordsLoading, setScanRecordsLoading] = useState(false);
  const [scanRecordsTotal, setScanRecordsTotal] = useState(0);
  const [scanRecordsPage, setScanRecordsPage] = useState(1);

  // 详情查看相关状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [viewingDoctor, setViewingDoctor] = useState<Doctor | null>(null);

  // 图片上传相关状态
  const [idCardFrontList, setIdCardFrontList] = useState<UploadFile[]>([]);
  const [idCardBackList, setIdCardBackList] = useState<UploadFile[]>([]);
  const [bankCardFrontList, setBankCardFrontList] = useState<UploadFile[]>([]);
  const [bankCardBackList, setBankCardBackList] = useState<UploadFile[]>([]);

  // Excel导入相关状态
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // 获取医生列表
  const fetchDoctors = async (params: DoctorQueryParams = {}) => {
    try {
      setLoading(true);
      const response = await doctorService.getDoctors({
        page: params.page || currentPage,
        pageSize: params.pageSize || pageSize,
        ...params
      });
      setDoctors(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('获取医生列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {

    try {
      const response = await getProjects();
      if (response.success) {
        setProjects(response.data);
      }
    } catch (error) {
      message.error('获取项目列表失败');
    }
  };
  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchDoctors({
      ...filters,
      page: currentPage,
      pageSize
    });
  }, [currentPage, pageSize, filters]);

  const handleSearch = (values: any) => {
    setFilters(values);
    setCurrentPage(1); // 重置到第一页
  };

  const handleAdd = () => {
    setEditingDoctor(null);
    form.resetFields();
    // 清空图片列表
    setIdCardFrontList([]);
    setIdCardBackList([]);
    setBankCardFrontList([]);
    setBankCardBackList([]);
    setIsModalVisible(true);
  };

  const handleEdit = (doctor: Doctor) => {
    setEditingDoctor(doctor);
    form.setFieldsValue(doctor);
    
    // 回显图片
    setIdCardFrontList(doctor.idCardFront ? [{
      uid: '-1',
      name: 'image.png',
      status: 'done',
      url: doctor.idCardFront,
    }] : []);
    setIdCardBackList(doctor.idCardBack ? [{
      uid: '-2',
      name: 'image.png',
      status: 'done',
      url: doctor.idCardBack,
    }] : []);
    setBankCardFrontList(doctor.bankCardFront ? [{
      uid: '-3',
      name: 'image.png',
      status: 'done',
      url: doctor.bankCardFront,
    }] : []);
    setBankCardBackList(doctor.bankCardBack ? [{
      uid: '-4',
      name: 'image.png',
      status: 'done',
      url: doctor.bankCardBack,
    }] : []);
    
    setIsModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这位医生吗？',
      onOk: async () => {
        try {
          await doctorService.deleteDoctor(id);
          fetchDoctors();
        } catch (error) {
          console.error('删除医生失败:', error);
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // 添加图片URL到表单数据
      const doctorData = {
        ...values,
        idCardFront: idCardFrontList[0]?.url || idCardFrontList[0]?.response?.url || null,
        idCardBack: idCardBackList[0]?.url || idCardBackList[0]?.response?.url || null,
        bankCardFront: bankCardFrontList[0]?.url || bankCardFrontList[0]?.response?.url || null,
        bankCardBack: bankCardBackList[0]?.url || bankCardBackList[0]?.response?.url || null,
      };
      
      if (editingDoctor) {
        await doctorService.updateDoctor(editingDoctor.id, doctorData);
        message.success('更新医生信息成功');
      } else {
        await doctorService.createDoctor(doctorData);
        message.success('添加医生成功');
      }
      setIsModalVisible(false);
      fetchDoctors();
    } catch (error) {
      console.error('保存医生信息失败:', error);
      message.error('保存医生信息失败');
    }
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleViewDetails = (doctor: Doctor) => {
    setViewingDoctor(doctor);
    setDetailModalVisible(true);
  };

  // 二维码相关处理函数
  const handleShowQRCode = async (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setQrCodeModalVisible(true);
    setQrCodeLoading(true);
    
    try {
      const qrData = await doctorService.getDoctorQRCode(doctor.id);
      setQrCodeData(qrData);
      
      // 获取扫码记录
      const scanData = await doctorService.getDoctorQRScanRecords(doctor.id, 1, 10);
      setScanRecords(scanData.records);
      setScanRecordsTotal(scanData.total);
      setScanRecordsPage(1);
    } catch (error) {
      message.error('获取二维码信息失败');
    } finally {
      setQrCodeLoading(false);
    }
  };

  const handleGenerateQRCode = async (doctor: Doctor) => {
    setQrCodeLoading(true);
    
    try {
      const qrData = await doctorService.generateDoctorQRCode(doctor.id);
      setQrCodeData(qrData);
      message.success('二维码生成成功');
      
      // 重新获取医生列表更新二维码状态
      fetchDoctors({ ...filters, page: currentPage, pageSize });
    } catch (error) {
      message.error('生成二维码失败');
    } finally {
      setQrCodeLoading(false);
    }
  };

  const handleScanRecordsPageChange = async (page: number) => {
    if (!selectedDoctor) return;
    
    setScanRecordsLoading(true);
    
    try {
      const scanData = await doctorService.getDoctorQRScanRecords(selectedDoctor.id, page, 10);
      setScanRecords(scanData.records);
      setScanRecordsPage(page);
    } catch (error) {
      message.error('获取扫码记录失败');
    } finally {
      setScanRecordsLoading(false);
    }
  };

  const handleBatchGenerateQRCodes = async () => {
    const selectedDoctorIds = doctors.filter(doctor => !doctor.wechatQrcode).map(doctor => doctor.id);
    
    if (selectedDoctorIds.length === 0) {
      message.info('所有医生都已有二维码');
      return;
    }
    
    try {
      const result = await doctorService.batchGenerateQRCodes(selectedDoctorIds);
      message.success(`批量生成完成：成功 ${result.success_count} 个，失败 ${result.failed_count} 个`);
      
      // 重新获取医生列表
      fetchDoctors({ ...filters, page: currentPage, pageSize });
    } catch (error) {
      message.error('批量生成二维码失败');
    }
  };

  // 图片上传配置
  const getUploadProps = (fileList: UploadFile[], setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>): UploadProps => ({
    name: 'file',
    action: `${axiosInstance.defaults.baseURL}/upload/image`,
    headers: {
      authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    listType: 'picture-card',
    fileList: fileList,
    maxCount: 1,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件！');
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('图片大小不能超过 5MB！');
      }
      return isImage && isLt5M;
    },
    onChange: (info) => {
      setFileList(info.fileList);
      if (info.file.status === 'done') {
        message.success(`${info.file.name} 上传成功`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} 上传失败`);
      }
    },
  });

  // Excel导入处理
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${axiosInstance.defaults.baseURL}/doctors/template/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'doctor_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error('下载模板失败');
    }
  };

  const handleImportExcel: UploadProps['onChange'] = async (info) => {
    if (info.file.status === 'uploading') {
      setImportLoading(true);
    }
    if (info.file.status === 'done') {
      setImportLoading(false);
      const result = info.file.response;
      if (result.success) {
        setImportResult(result.data);
        message.success('导入完成！');
        // 刷新医生列表
        fetchDoctors({ ...filters, page: currentPage, pageSize });
      } else {
        message.error(result.message || '导入失败');
      }
    } else if (info.file.status === 'error') {
      setImportLoading(false);
      message.error(`${info.file.name} 文件上传失败`);
    }
  };

  const handleShowImportModal = () => {
    setImportResult(null);
    setImportModalVisible(true);
  };

  const columns: ColumnsType<Doctor> = [



    {
      title: '医院名称',
      dataIndex: 'hospitalName',
      key: 'hospitalName',
    },

    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: "身份证号",
      dataIndex: "idCard",
      key: "idCard",
    },

    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '手机绑定状态',
      dataIndex: 'phoneBindingStatus',
      key: 'phoneBindingStatus',
      render: (status: boolean) => status ? '已绑定' : '未绑定'
    },
    {
      title: '微信二维码',
      dataIndex: 'wechatQrcode',
      key: 'wechatQrcode',
      render: (qrcode: string) => (
        <Tag color={qrcode ? 'green' : 'orange'}>
          {qrcode ? '已生成' : '未生成'}
        </Tag>
      )
    },
    {
      title: '证件照片',
      key: 'images',
      render: (_, record) => (
        <Space size="small">
          {record.idCardFront && (
            <Image
              width={50}
              height={30}
              src={record.idCardFront}
              alt="身份证正面"
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              preview={{
                mask: <div style={{ fontSize: '12px' }}>身份证正面</div>
              }}
            />
          )}
          {record.idCardBack && (
            <Image
              width={50}
              height={30}
              src={record.idCardBack}
              alt="身份证背面"
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              preview={{
                mask: <div style={{ fontSize: '12px' }}>身份证背面</div>
              }}
            />
          )}
          {record.bankCardFront && (
            <Image
              width={50}
              height={30}
              src={record.bankCardFront}
              alt="银行卡正面"
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              preview={{
                mask: <div style={{ fontSize: '12px' }}>银行卡正面</div>
              }}
            />
          )}
          {record.bankCardBack && (
            <Image
              width={50}
              height={30}
              src={record.bankCardBack}
              alt="银行卡背面"
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              preview={{
                mask: <div style={{ fontSize: '12px' }}>银行卡背面</div>
              }}
            />
          )}
          {!record.idCardFront && !record.idCardBack && !record.bankCardFront && !record.bankCardBack && (
            <span style={{ color: '#999', fontSize: '12px' }}>暂无照片</span>
          )}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            查看详情
          </Button>
          <Button
            type="default"
            icon={<QrcodeOutlined />}
            onClick={() => handleShowQRCode(record)}
          >
            二维码
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 移动端卡片渲染
  const renderMobileCard = (doctor: Doctor) => (
    <Card 
      key={doctor.id}
      style={{ marginBottom: 16 }}
      size="small"
    >
      <div style={{ marginBottom: 8 }}>
        <strong>姓名：</strong>{doctor.name}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>医院：</strong>{doctor.hospitalName}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>电话：</strong>{doctor.phone}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>身份证号：</strong>{doctor.idCard}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>绑定状态：</strong>
        <Tag color={doctor.phoneBindingStatus ? 'success' : 'default'}>
          {doctor.phoneBindingStatus ? '已绑定' : '未绑定'}
        </Tag>
      </div>
      <div style={{ marginTop: 12 }}>
        <Space wrap size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(doctor)}
          >
            编辑
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(doctor)}
          >
            详情
          </Button>
          <Button
            size="small"
            icon={<QrcodeOutlined />}
            onClick={() => handleShowQRCode(doctor)}
          >
            二维码
          </Button>
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(doctor.id)}
          >
            删除
          </Button>
        </Space>
      </div>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>


      {/* 搜索表单 */}
      <Form
        form={searchForm}
        layout={isMobile ? 'vertical' : 'inline'}
        onFinish={handleSearch}
        style={{ marginBottom: '16px' }}
      >
        <Form.Item name="name" label="医生姓名">
          <Input placeholder="请输入医生姓名" />
        </Form.Item>
        <Form.Item name="project_id" label="项目">
          <Select style={{ width: isMobile ? '100%' : 200 }} allowClear>
            
            {projects.map((project) => (
              <Select.Option key={project.id} value={project.id}>
                {project.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />} block={isMobile}>
            搜索
          </Button>
        </Form.Item>
      </Form>

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px' }}>
        <Space wrap size={isMobile ? 'small' : 'middle'}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '添加' : '添加医生'}
          </Button>
          <Button
            type="default"
            icon={<UploadOutlined />}
            onClick={handleShowImportModal}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '导入' : '导入医生'}
          </Button>
          <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '模板' : '下载模板'}
          </Button>
          <Button
            type="default"
            icon={<QrcodeOutlined />}
            onClick={handleBatchGenerateQRCodes}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '二维码' : '批量生成二维码'}
          </Button>
        </Space>
      </div>

      {/* 医生列表 - PC端表格/移动端卡片 */}
      {!isMobile ? (
        <Table
          columns={columns}
          dataSource={doctors}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          onChange={handleTableChange}
        />
      ) : (
        <>
          <List
            loading={loading}
            dataSource={doctors}
            renderItem={renderMobileCard}
          />
          {/* 移动端分页 */}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ fontSize: 12, color: '#666' }}>
                共 {total} 条记录，第 {currentPage}/{Math.ceil(total / pageSize)} 页
              </div>
              <Space>
                <Button
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  上一页
                </Button>
                <Button
                  size="small"
                  disabled={currentPage >= Math.ceil(total / pageSize)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  下一页
                </Button>
              </Space>
            </Space>
          </div>
        </>
      )}

      {/* 编辑/添加模态框 */}
      <Modal
        title={editingDoctor ? '编辑医生' : '添加医生'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="hospitalProvince"
            label="医院省份"
            rules={[{ required: true, message: '请输入医院省份！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="hospitalCity"
            label="医院城市"
            rules={[{ required: true, message: '请输入医院城市！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="hospitalName"
            label="医院名称"
            rules={[{ required: true, message: '请输入医院名称！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="departmentName"
            label="科室名称"
            rules={[{ required: true, message: '请输入科室名称！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="title"
            label="职称"
            rules={[{ required: true, message: '请输入职称！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="gender"
            label="性别"
            rules={[{ required: true, message: '请选择性别！' }]}
          >
            <Select>
              <Select.Option value="男">男</Select.Option>
              <Select.Option value="女">女</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="idCard"
            label="身份证号"
            rules={[
              { required: true, message: '请输入身份证号！' },
              { pattern: /^\d{17}[\dX]$/, message: '请输入有效的身份证号！' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="联系电话"
            rules={[
              { required: true, message: '请输入联系电话！' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码！' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phoneBindingStatus"
            label="手机绑定状态"
          >
            <Select>
              <Select.Option value={true}>已绑定</Select.Option>
              <Select.Option value={false}>未绑定</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="bankName"
            label="开户行"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="bankAccount"
            label="银行账号"
            rules={[
              { pattern: /^\d{16,19}$/, message: '请输入有效的银行账号！' }
            ]}
          >
            <Input />
          </Form.Item>

          {/* 证件照片上传 */}
          <div style={{ marginTop: '20px', marginBottom: '10px', fontWeight: 'bold' }}>
            证件照片
          </div>
          
          <Form.Item label="身份证正面">
            <Upload {...getUploadProps(idCardFrontList, setIdCardFrontList)}>
              {idCardFrontList.length < 1 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item label="身份证反面">
            <Upload {...getUploadProps(idCardBackList, setIdCardBackList)}>
              {idCardBackList.length < 1 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item label="银行卡正面">
            <Upload {...getUploadProps(bankCardFrontList, setBankCardFrontList)}>
              {bankCardFrontList.length < 1 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item label="银行卡反面">
            <Upload {...getUploadProps(bankCardBackList, setBankCardBackList)}>
              {bankCardBackList.length < 1 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 二维码模态框 */}
      <Modal
        title={`医生二维码 - ${selectedDoctor?.name || ''}`}
        open={qrCodeModalVisible}
        onCancel={() => {
          setQrCodeModalVisible(false);
          setSelectedDoctor(null);
          setQrCodeData(null);
          setScanRecords([]);
        }}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        <Tabs defaultActiveKey="qrcode">
          <Tabs.TabPane tab="二维码" key="qrcode">
            <div style={{ textAlign: 'center' }}>
              {qrCodeLoading ? (
                <div style={{ padding: '50px' }}>加载中...</div>
              ) : qrCodeData ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Image
                      width={200}
                      height={200}
                      src={qrCodeData.qr_code_url}
                      alt="医生二维码"
                      style={{ border: '1px solid #d9d9d9' }}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <p><strong>医生姓名：</strong>{qrCodeData.doctor_name}</p>
                    <p><strong>场景值：</strong>{qrCodeData.scene_str}</p>
                    {qrCodeData.is_auto_generated && (
                      <Tag color="blue">自动生成</Tag>
                    )}
                  </div>
                  <div>
                    <Button
                      type="primary"
                      icon={<QrcodeOutlined />}
                      onClick={() => selectedDoctor && handleGenerateQRCode(selectedDoctor)}
                      loading={qrCodeLoading}
                    >
                      重新生成
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{ padding: '50px' }}>
                  <p>该医生尚未生成二维码</p>
                  <Button
                    type="primary"
                    icon={<QrcodeOutlined />}
                    onClick={() => selectedDoctor && handleGenerateQRCode(selectedDoctor)}
                    loading={qrCodeLoading}
                  >
                    生成二维码
                  </Button>
                </div>
              )}
            </div>
          </Tabs.TabPane>
          
          <Tabs.TabPane tab={`扫码记录 (${scanRecordsTotal})`} key="records">
            <Table
              dataSource={scanRecords}
              loading={scanRecordsLoading}
              pagination={{
                current: scanRecordsPage,
                total: scanRecordsTotal,
                pageSize: 10,
                onChange: handleScanRecordsPageChange,
                showTotal: (total) => `共 ${total} 条记录`
              }}
              columns={[
                {
                  title: '微信用户ID',
                  dataIndex: 'openid',
                  key: 'openid',
                  render: (text: string) => text || '未知'
                },
                {
                  title: '关注时间',
                  dataIndex: 'subscribe_time',
                  key: 'subscribe_time',
                  render: (time: string) => new Date(time).toLocaleString()
                },
                {
                  title: '状态',
                  dataIndex: 'is_unsubscribed',
                  key: 'is_unsubscribed',
                  render: (isUnsubscribed: boolean) => (
                    <Tag color={isUnsubscribed ? 'red' : 'green'}>
                      {isUnsubscribed ? '已取消关注' : '已关注'}
                    </Tag>
                  )
                }
              ]}
              rowKey="id"
              size="small"
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 医生详情查看模态框 */}
      <Modal
        title={`医生详情 - ${viewingDoctor?.name || ''}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setViewingDoctor(null);
        }}
        footer={null}
        width={isMobile ? '100%' : 1000}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        {viewingDoctor && (
          <div>
            {/* 基本信息 */}
            <div style={{ marginBottom: '24px' }}>
              <h3>基本信息</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div><strong>姓名：</strong>{viewingDoctor.name}</div>
                <div><strong>性别：</strong>{viewingDoctor.gender}</div>
                <div><strong>职称：</strong>{viewingDoctor.title}</div>
                <div><strong>身份证号：</strong>{viewingDoctor.idCard}</div>
                <div><strong>联系电话：</strong>{viewingDoctor.phone}</div>
                <div><strong>手机绑定：</strong>{viewingDoctor.phoneBindingStatus ? '已绑定' : '未绑定'}</div>
              </div>
            </div>

            {/* 医院信息 */}
            <div style={{ marginBottom: '24px' }}>
              <h3>医院信息</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div><strong>医院省份：</strong>{viewingDoctor.hospitalProvince}</div>
                <div><strong>医院城市：</strong>{viewingDoctor.hospitalCity}</div>
                <div><strong>医院名称：</strong>{viewingDoctor.hospitalName}</div>
                <div><strong>科室名称：</strong>{viewingDoctor.departmentName}</div>
              </div>
            </div>

            {/* 银行信息 */}
            {(viewingDoctor.bankName || viewingDoctor.bankAccount) && (
              <div style={{ marginBottom: '24px' }}>
                <h3>银行信息</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div><strong>开户行：</strong>{viewingDoctor.bankName || '未填写'}</div>
                  <div><strong>银行账号：</strong>{viewingDoctor.bankAccount || '未填写'}</div>
                </div>
              </div>
            )}

            {/* 证件照片 */}
            <div style={{ marginBottom: '24px' }}>
              <h3>证件照片</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {/* 身份证照片 */}
                <div>
                  <h4>身份证正面</h4>
                  {viewingDoctor.idCardFront ? (
                    <Image
                      width={200}
                      height={120}
                      src={viewingDoctor.idCardFront}
                      alt="身份证正面"
                      style={{ objectFit: 'cover', borderRadius: '8px', border: '1px solid #d9d9d9' }}
                    />
                  ) : (
                    <div style={{ 
                      width: 200, 
                      height: 120, 
                      border: '1px dashed #d9d9d9', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999'
                    }}>
                      暂无照片
                    </div>
                  )}
                </div>

                <div>
                  <h4>身份证反面</h4>
                  {viewingDoctor.idCardBack ? (
                    <Image
                      width={200}
                      height={120}
                      src={viewingDoctor.idCardBack}
                      alt="身份证反面"
                      style={{ objectFit: 'cover', borderRadius: '8px', border: '1px solid #d9d9d9' }}
                    />
                  ) : (
                    <div style={{ 
                      width: 200, 
                      height: 120, 
                      border: '1px dashed #d9d9d9', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999'
                    }}>
                      暂无照片
                    </div>
                  )}
                </div>

                {/* 银行卡照片 */}
                <div>
                  <h4>银行卡正面</h4>
                  {viewingDoctor.bankCardFront ? (
                    <Image
                      width={200}
                      height={120}
                      src={viewingDoctor.bankCardFront}
                      alt="银行卡正面"
                      style={{ objectFit: 'cover', borderRadius: '8px', border: '1px solid #d9d9d9' }}
                    />
                  ) : (
                    <div style={{ 
                      width: 200, 
                      height: 120, 
                      border: '1px dashed #d9d9d9', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999'
                    }}>
                      暂无照片
                    </div>
                  )}
                </div>

                <div>
                  <h4>银行卡反面</h4>
                  {viewingDoctor.bankCardBack ? (
                    <Image
                      width={200}
                      height={120}
                      src={viewingDoctor.bankCardBack}
                      alt="银行卡反面"
                      style={{ objectFit: 'cover', borderRadius: '8px', border: '1px solid #d9d9d9' }}
                    />
                  ) : (
                    <div style={{ 
                      width: 200, 
                      height: 120, 
                      border: '1px dashed #d9d9d9', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999'
                    }}>
                      暂无照片
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Excel导入模态框 */}
      <Modal
        title="导入医生信息"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '10px' }}>📝 导入说明：</p>
            <ul style={{ paddingLeft: '20px', color: '#666' }}>
              <li>请先下载Excel模板，按照模板格式填写医生信息</li>
              <li>根据<strong>身份证号</strong>判断医生是否存在，存在则更新，不存在则创建</li>
              <li><strong>必填字段：</strong>医院省份、医院城市、医院名称、科室名称、姓名、职称、性别、身份证号、联系电话</li>
              <li><strong>选填字段：</strong>开户行、银行账号</li>
              <li>支持的文件格式：.xls 或 .xlsx</li>
              <li>文件大小限制：10MB</li>
            </ul>
          </div>

          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              下载Excel模板
            </Button>
          </div>

          <Upload
            name="file"
            action={`${axiosInstance.defaults.baseURL}/doctors/import/excel`}
            headers={{
              authorization: `Bearer ${localStorage.getItem('token')}`,
            }}
            accept=".xls,.xlsx"
            showUploadList={false}
            onChange={handleImportExcel}
          >
            <Button
              type="default"
              icon={<UploadOutlined />}
              loading={importLoading}
              block
            >
              {importLoading ? '正在导入...' : '选择Excel文件并导入'}
            </Button>
          </Upload>

          {importResult && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <h4 style={{ marginBottom: '10px' }}>导入结果：</h4>
              <p>总计：{importResult.total} 条</p>
              <p style={{ color: '#52c41a' }}>成功：{importResult.success_count} 条</p>
              <p style={{ color: '#ff4d4f' }}>失败：{importResult.failed_count} 条</p>
              
              {importResult.failed_records && importResult.failed_records.length > 0 && (
                <div style={{ marginTop: '15px' }}>
                  <h5>失败记录：</h5>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {importResult.failed_records.map((record: any, index: number) => (
                      <div key={index} style={{ padding: '8px', borderBottom: '1px solid #e8e8e8' }}>
                        <p style={{ margin: 0 }}>
                          <strong>第{record.row}行 - {record.name}：</strong>
                          <span style={{ color: '#ff4d4f' }}>{record.reason}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DoctorManagement; 